/**
 * The status action matrix for a tenant membership (ORCH-420 §3.2)
 *
 * Purpose: One table decides which actions a membership's status allows, so the
 *          list's Actions cell, its checkboxes and bulk bar, and the user page
 *          header all agree, and nothing branches on an invite anywhere else.
 *          An invitation is a membership with status `invited`: it is opened and
 *          edited like any member, and its own actions are Resend Invite and
 *          Cancel Invite; Deactivate, Reactivate, Remove, a password reset and
 *          Effective Access don't apply to someone who hasn't joined yet.
 * Logic: Each action names the statuses it applies to and the permission that
 *        allows it: the caller's permission in the tenant (`can`) and the server's
 *        row flag (`_authz`), which is the caller's scope on *that* row. Changing
 *        an invitation takes either `user.edit` or `user.create` reaching it
 *        (`can_edit` or `can_invite`), so those actions list two permissions.
 *        The backend keeps the same rules as preconditions in each service
 *        operation (INVITE_PENDING, NOT_INVITED); this is the client's copy.
 */

import { PermissionKey } from '../../constants/permissionKeys';
import { Member, MemberFlags, MembershipStatus } from '../../types/userManagement';

export type MemberAction =
  | 'edit'
  | 'assignRoles'
  | 'editNames'
  | 'deactivate'
  | 'reactivate'
  | 'remove'
  | 'passwordReset'
  | 'effectiveAccess'
  | 'resendInvite'
  | 'cancelInvite';

/** One way an action may be allowed: the permission held, and the row flag that reports it. */
interface Grant {
  permission: PermissionKey;
  /** The `_authz` flag for the permission; none when the action isn't row-scoped. */
  flag?: keyof MemberFlags;
}

export interface MemberActionRule {
  /** The membership statuses the action applies to. */
  statuses: readonly MembershipStatus[];
  /** Any one of these grants allows the action. */
  grants: readonly Grant[];
}

const EDIT: Grant = { permission: PermissionKey.USER_EDIT, flag: 'can_edit' };
const INVITE: Grant = { permission: PermissionKey.USER_CREATE, flag: 'can_invite' };
const DEACTIVATE: Grant = { permission: PermissionKey.USER_DEACTIVATE, flag: 'can_deactivate' };
const ASSIGN: Grant = { permission: PermissionKey.USER_ASSIGN_ROLES, flag: 'can_assign_roles' };
const AUDIT: Grant = { permission: PermissionKey.AUDIT_VIEW };

/** The matrix: which statuses each action applies to, and what allows it. */
export const MEMBER_ACTIONS: Readonly<Record<MemberAction, MemberActionRule>> = {
  edit: { statuses: ['active', 'inactive', 'invited'], grants: [EDIT, INVITE] },
  assignRoles: { statuses: ['active', 'inactive', 'invited'], grants: [ASSIGN] },
  // The profile belongs to the person; only an invitee's typed name is an admin's to correct
  editNames: { statuses: ['invited'], grants: [EDIT, INVITE] },
  deactivate: { statuses: ['active'], grants: [DEACTIVATE] },
  reactivate: { statuses: ['inactive'], grants: [DEACTIVATE] },
  remove: { statuses: ['active', 'inactive'], grants: [DEACTIVATE] },
  passwordReset: { statuses: ['active'], grants: [EDIT] },
  // An invitation has no entitlements yet, so there is nothing to explain
  effectiveAccess: { statuses: ['active', 'inactive'], grants: [AUDIT] },
  resendInvite: { statuses: ['invited'], grants: [EDIT, INVITE] },
  cancelInvite: { statuses: ['invited'], grants: [EDIT, INVITE] },
};

type Can = (permission: PermissionKey) => boolean;
type Subject = Pick<Member, 'status' | '_authz'>;

/** Does one of the action's grants hold for this row? */
const granted = (rule: MemberActionRule, member: Subject, can: Can): boolean =>
  rule.grants.some(
    (grant) => can(grant.permission) && (!grant.flag || !!member._authz?.[grant.flag])
  );

/** Whether the action applies to the status at all, whoever asks. */
export const actionAppliesTo = (action: MemberAction, status: string): boolean =>
  (MEMBER_ACTIONS[action].statuses as readonly string[]).includes(status);

/** Whether the caller may take the action on this row: status, permission and row flag. */
export const mayAct = (action: MemberAction, member: Subject, can: Can): boolean =>
  actionAppliesTo(action, member.status) && granted(MEMBER_ACTIONS[action], member, can);

/** The set of actions the caller may take on this row. */
export const memberActions = (member: Subject, can: Can): Set<MemberAction> =>
  new Set(
    (Object.keys(MEMBER_ACTIONS) as MemberAction[]).filter((action) => mayAct(action, member, can))
  );

/** The actions the bulk bar offers, in order, and the statuses each acts on. */
export const BULK_ACTIONS = [
  'assignRoles',
  'deactivate',
  'reactivate',
  'resendInvite',
  'cancelInvite',
] as const;
export type BulkMemberAction = (typeof BULK_ACTIONS)[number];

/** A row can be ticked when some bulk action could act on it. */
export const isBulkSelectable = (member: Subject, can: Can): boolean =>
  BULK_ACTIONS.some((action) => mayAct(action, member, can));
